import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, Paper, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ReferenceArea, ReferenceDot,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Live trade tracker (v0.47.0, prompt 10/6/2026): "how far from the goal, how
// far from danger", refreshed every minute while a spread is held.
// Source: logs/opt_spy_daily_checkins_YYYY-MM-DD.jsonl via the backend feed.
// Rules (from the prompt): poll at 60 s, never faster; a missing file or a
// last row older than 3 minutes = nothing to track (never extrapolate); the
// chart is informational — the engine's exits are mechanical and already
// running; the Trade log stays the record of what actually happened.
// v0.63.0 (10/21): after the account is flat the engine keeps writing
// after_exit=true rows (one/min until 16:00) marking the closed spread to
// market — the panel stays open all day: real line solid to exit_at, a
// marker at the exit, then a dashed grey "if held" line. upl/captured on
// those rows are if-held numbers and never reach any P&L.
// ─────────────────────────────────────────────────────────────────────────────

interface CheckinRow {
  ts_et: string;
  account: string;
  direction?: 'put_credit' | 'call_credit';
  short_strike?: number;
  long_strike?: number;
  contracts?: number;
  opened_at?: string;
  entry_credit?: number;
  mid?: number;
  captured?: number;
  upl?: number;
  best_mid?: number;
  spot?: number;
  exit_level?: number;
  exit_level_kind?: string;
  put_wall?: number;
  call_wall?: number;
  flip?: number;
  profit_close_limit?: number | null;
  stop_debit?: number;
  forced_close_et?: string;
  giveback_armed?: boolean;
  // v0.56.0: entry grade + the grade B/C profit floor (credit × 0.70);
  // armed once 35% was captured — rows before v0.56.0 have none of these
  entry_grade?: 'A' | 'B' | 'C' | null;
  entry_grade_flags?: string[] | null;
  profit_floor_debit?: number | null;
  profit_floor_armed?: boolean | null;
  // v0.69.0: the floor fired and the AI now decides (no exit on the line
  // by itself); profit_floor_debit is also null on credits ≤ $0.10
  floor_ai_watching?: boolean | null;
  // v0.63.0: after the account is flat the engine keeps writing one row per
  // minute until 16:00, marking the closed spread as if still open. On these
  // rows upl/captured/best_mid are the IF-HELD numbers — never P&L. Older
  // rows have no after_exit field — treat as live.
  after_exit?: boolean | null;
  exit_at?: string | null;
  exit_debit?: number | null;
  exit_reason?: string | null;
  exit_pnl_per_ct?: number | null;
  if_held_pnl_per_ct?: number | null;
  if_held_vs_exit_per_ct?: number | null;
  if_held_goal_hit_at?: string | null; // sticky HH:MM, first touch of the goal
  if_held_stop_hit_at?: string | null; // sticky HH:MM, first touch of the 4× stop
}

interface TrackerResp {
  date: string;
  exists: boolean;
  stale: boolean;
  latest_ts: string | null;
  accounts: { [account: string]: CheckinRow[] };
  // v0.69.0: FLOOR_AI_VOTE rows (leader only) + per-account watch/fallback/exit
  floor_ai?: {
    votes: { account?: string; ts?: string; ts_et?: string; opened_at?: string; mid?: number; verdict?: string; confidence?: number; breach_prob?: number; misread?: any; error?: any; reason?: string }[];
    watch: { [account: string]: { [k: string]: any } };
    fallback: { [account: string]: { [k: string]: any } };
    exit: { [account: string]: { [k: string]: any } };
  };
}

const toMs = (iso?: string): number => (iso ? new Date(iso).getTime() : 0);
const hhmm = (ms: number): string => {
  const d = new Date(ms);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/New_York' });
};
const cents = (v?: number | null): string => (v == null ? '—' : `${Math.round(v * 100)}¢`);

const LiveTradeTracker: React.FC<{ instanceName: string }> = ({ instanceName }) => {
  const { token } = useAuth();
  const [data, setData] = useState<TrackerResp | null>(null);
  const [account, setAccount] = useState<string>('Ilan-Live');

  const fetchData = useCallback(async () => {
    try {
      const r = await fetch(`/api/instances/${instanceName}/opt-spy-live-tracker`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } catch { /* transient — next poll retries */ }
  }, [instanceName, token]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60_000); // poll at 60 s; never faster
    return () => clearInterval(id);
  }, [fetchData]);

  const accounts = useMemo(() => Object.keys(data?.accounts || {}), [data]);
  const rows = useMemo(() => {
    const key = data?.accounts?.[account] ? account : accounts[0];
    return (key && data?.accounts?.[key]) || [];
  }, [data, account, accounts]);
  const last = rows.length ? rows[rows.length - 1] : null;

  // No file yet (no trade today, or the engine hasn't opened a spread) —
  // show a thin "armed" strip so the operator knows the tracker is wired,
  // but never render chart data that doesn't exist.
  if (!data?.exists || !last) {
    if (!data) return null; // first fetch still in flight
    return (
      <Paper sx={{ p: 1, mb: 2, opacity: 0.75 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Live trade tracker: no open position — the two 1-minute charts (spread mid vs goal/stop,
          SPY vs the exit line) appear here within a minute of the first open spread. (v0.47.0 —
          the engine writes <code>opt_spy_daily_checkins_*.jsonl</code> once a minute while a spread is held.)
        </Typography>
      </Paper>
    );
  }
  const live = !data.stale;
  const isPut = last.direction !== 'call_credit';
  // v0.63.0: once flat, rows keep coming with after_exit=true until 16:00 —
  // the panel stays open all day; the real series ends at exit_at and a
  // dashed grey "if held" line carries on
  const afterExit = !!last.after_exit;
  const lastLive = [...rows].reverse().find((r) => !r.after_exit) ?? last;
  const lastLiveIdx = rows.lastIndexOf(lastLive);
  const prettyExit = (r?: string | null) => (r === 'floor_ai_exit' ? 'floor AI' : (r || 'exit').replace(/_/g, ' ').replace(/\bai\b/i, 'AI'));

  const chartData = rows.map((r, i) => ({
    t: toMs(r.ts_et),
    // solid real line up to the exit; dashed if-held line after (seeded at
    // the last live row so the two series join without a gap)
    midC: !r.after_exit && r.mid != null ? r.mid * 100 : null,
    heldC: r.after_exit && r.mid != null ? r.mid * 100
      : i === lastLiveIdx && afterExit && r.mid != null ? r.mid * 100 : null,
    spot: r.spot ?? null,
  }));
  const exitMs = afterExit && last.exit_at ? toMs(last.exit_at) : null;
  const goalC = last.profit_close_limit != null ? last.profit_close_limit * 100 : null;
  const breakevenC = (last.entry_credit ?? 0) * 100;
  const stopC = (last.stop_debit ?? 0) * 100;
  const maxMidC = Math.max(...chartData.map((d) => d.midC ?? 0), stopC) * 1.12;

  // v0.69.0 floor AI: leader votes as dots from the fire on (green HOLD,
  // red EXIT, grey error/misread), only for this trade and this account
  const acctKey = data.accounts[account] ? account : accounts[0];
  const floorWatching = rows.some((r) => r.floor_ai_watching) || !!data.floor_ai?.watch?.[acctKey];
  const t0 = chartData.length ? chartData[0].t : 0;
  const t1 = chartData.length ? chartData[chartData.length - 1].t : 0;
  const voteDots = (data.floor_ai?.votes || [])
    .filter((v) => v.account === acctKey && (!v.opened_at || !lastLive.opened_at || v.opened_at === lastLive.opened_at) && v.mid != null)
    .map((v) => {
      const bad = !!(v.error || v.misread);
      return {
        t: toMs(v.ts_et || v.ts), y: (v.mid as number) * 100,
        color: bad ? '#9e9e9e' : String(v.verdict).toUpperCase() === 'EXIT' ? '#ef5350' : '#66bb6a',
      };
    })
    .filter((d) => d.t >= t0 && d.t <= t1);

  // Readouts
  const toGoalC = last.mid != null && last.profit_close_limit != null
    ? Math.round((last.mid - last.profit_close_limit) * 100) : null;
  const capturedPct = last.captured != null ? Math.round(last.captured * 100) : null;
  const givingBack = last.mid != null && last.best_mid != null && last.mid - last.best_mid >= 0.01;
  const exitGapPts = last.spot != null && last.exit_level != null
    ? (isPut ? last.spot - last.exit_level : last.exit_level - last.spot) : null;
  const exitGapPct = exitGapPts != null && last.spot ? (exitGapPts / last.spot) * 100 : null;
  const nearExit = exitGapPct != null && exitGapPct < 0.15;

  // after the exit the clock stops at exit_at — "held 1h 44m", not still counting
  const minsOpen = last.opened_at
    ? Math.max(0, Math.round(((afterExit && last.exit_at ? toMs(last.exit_at) : toMs(last.ts_et)) - toMs(last.opened_at)) / 60000))
    : null;
  const spotVals = chartData.map((d) => d.spot).filter((v): v is number => v != null);
  const structLines = [last.exit_level, last.short_strike, last.put_wall, last.call_wall, last.flip].filter((v): v is number => v != null);
  const spotMin = Math.min(...spotVals, ...structLines) - 0.5;
  const spotMax = Math.max(...spotVals, ...structLines) + 0.5;

  return (
    <Paper sx={{ p: 2, mb: 2, border: live && !afterExit ? '1px solid rgba(102,187,106,0.5)' : undefined }}>
      {/* Header strip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {afterExit ? 'Out of the trade — tracking "if held"' : live ? 'Live trade' : 'Last trade (closed)'}
        </Typography>
        <Chip
          label={afterExit
            ? `out at ${last.exit_at ? hhmm(toMs(last.exit_at)) : '—'} ET${live ? ' — if-held updates every minute until 16:00' : ''}`
            : live ? 'LIVE — updates every minute' : `last check-in ${data.latest_ts ? hhmm(toMs(data.latest_ts)) : '—'} ET`}
          size="small" color={live && !afterExit ? 'success' : 'default'} variant={live && !afterExit ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'bold' }}
        />
        {/* v0.56.0: entry grade — big coloured letter, flags spelled out
            (after-exit rows may not carry it — read the last live row) */}
        {lastLive.entry_grade && (
          <Chip
            label={`${lastLive.entry_grade}${lastLive.entry_grade_flags?.length
              ? ` — ${lastLive.entry_grade_flags.map((f) =>
                  f === 'iv' ? 'IV > 16.5%' : f === 'mixed' ? 'MIXED regime' : f === 'cushion' ? 'near the exit line' : f).join(' · ')}`
              : ' — clean entry'}`}
            size="small"
            sx={{
              fontWeight: 'bold',
              bgcolor: lastLive.entry_grade === 'A' ? '#66bb6a' : lastLive.entry_grade === 'B' ? '#ffb74d' : '#ef5350',
              color: '#000',
            }}
          />
        )}
        <Chip label={`${last.short_strike ?? '?'}/${last.long_strike ?? '?'}${isPut ? 'p' : 'c'} × ${last.contracts ?? '?'}`} size="small" variant="outlined" />
        {minsOpen != null && <Chip label={`${afterExit ? 'held' : 'open'} ${Math.floor(minsOpen / 60)}h ${minsOpen % 60}m`} size="small" variant="outlined" />}
        {!afterExit && last.forced_close_et && <Chip label={`forced close ${last.forced_close_et} ET`} size="small" variant="outlined" />}
        <Chip
          label={afterExit
            ? `would-be close target $${last.profit_close_limit != null ? last.profit_close_limit.toFixed(2) : '—'}`
            : last.profit_close_limit == null ? 'no resting order — repricing' : `resting close parked @ $${last.profit_close_limit.toFixed(2)}`}
          size="small" variant="outlined"
          color={!afterExit && last.profit_close_limit == null ? 'warning' : 'default'}
        />
        {accounts.length > 1 && (
          <ToggleButtonGroup
            size="small" exclusive value={data.accounts[account] ? account : accounts[0]}
            onChange={(_, v) => v && setAccount(v)} sx={{ ml: 'auto' }}
          >
            {accounts.map((a) => <ToggleButton key={a} value={a} sx={{ py: 0, px: 1, fontSize: '0.7rem', textTransform: 'none' }}>{a}</ToggleButton>)}
          </ToggleButtonGroup>
        )}
      </Box>

      {/* 1 — Spread price (the money chart) */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 480px', minWidth: 320 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Spread mid (what it costs to buy back — lower is better)
          </Typography>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 2, left: 0 }}>
              <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={hhmm} tick={{ fontSize: 10 }} />
              <YAxis domain={[0, Math.ceil(maxMidC)]} tickFormatter={(v) => `${v}¢`} tick={{ fontSize: 10 }} width={36} />
              <Tooltip
                labelFormatter={(t) => `${hhmm(t as number)} ET`}
                formatter={(v: any, name: any) => [`${Number(v).toFixed(1)}¢`, name === 'heldC' ? 'if held' : 'mid']}
              />
              {/* green between goal and breakeven, red above breakeven, deep red above stop */}
              {goalC != null && <ReferenceArea y1={goalC} y2={breakevenC} fill="#66bb6a" fillOpacity={0.08} />}
              <ReferenceArea y1={breakevenC} y2={stopC} fill="#ef5350" fillOpacity={0.06} />
              <ReferenceArea y1={stopC} y2={Math.ceil(maxMidC)} fill="#b71c1c" fillOpacity={0.12} />
              {goalC != null && (
                <ReferenceLine y={goalC} stroke="#66bb6a" strokeDasharray="6 3"
                  label={{ value: `resting close $${(goalC / 100).toFixed(2)} — fills here`, fontSize: 10, fill: '#66bb6a', position: 'insideBottomLeft' }} />
              )}
              <ReferenceLine y={breakevenC} stroke="#9e9e9e"
                label={{ value: `sold for $${(breakevenC / 100).toFixed(2)}`, fontSize: 10, fill: '#9e9e9e', position: 'insideTopLeft' }} />
              {/* v0.56.0: grade B/C profit floor (credit × 0.70) — the engine
                  exits here once 35% was captured; faint until armed.
                  Grade A has no line: the trade runs to the goal. */}
              {last.profit_floor_debit != null && (
                <ReferenceLine
                  y={last.profit_floor_debit * 100}
                  stroke="#ffb74d"
                  strokeDasharray="6 3"
                  strokeOpacity={lastLive.profit_floor_armed || floorWatching ? 1 : 0.35}
                  label={{
                    value: floorWatching ? 'floor 30% — AI decides' : lastLive.profit_floor_armed ? 'floor 30%' : 'floor 30% — arms at 35%',
                    fontSize: 10, fill: '#ffb74d', position: 'insideRight',
                    opacity: lastLive.profit_floor_armed || floorWatching ? 1 : 0.5,
                  }}
                />
              )}
              {voteDots.map((d, i) => (
                <ReferenceDot key={i} x={d.t} y={d.y} r={3.5} fill={d.color} stroke="none" ifOverflow="discard" />
              ))}
              <ReferenceLine y={stopC} stroke="#ef5350"
                label={{ value: `4× stop $${(stopC / 100).toFixed(2)}`, fontSize: 10, fill: '#ef5350', position: 'insideTopLeft' }} />
              {/* v0.63.0: marker at the real exit, then the dashed grey if-held line */}
              {exitMs != null && (
                <ReferenceLine x={exitMs} stroke="#e0e0e0" strokeDasharray="2 2"
                  label={{
                    value: `${prettyExit(last.exit_reason)} $${last.exit_debit != null ? last.exit_debit.toFixed(2) : '—'}${last.exit_pnl_per_ct != null ? ` · ${last.exit_pnl_per_ct >= 0 ? '+' : '−'}$${Math.abs(last.exit_pnl_per_ct).toFixed(0)}/ct` : ''}`,
                    fontSize: 10, fill: '#e0e0e0', position: 'insideTopRight',
                  }} />
              )}
              <Line type="stepAfter" dataKey="midC" stroke="#42a5f5" dot={false} strokeWidth={2} isAnimationActive={false} connectNulls />
              {afterExit && (
                <Line type="stepAfter" dataKey="heldC" stroke="#9e9e9e" strokeDasharray="5 4" dot={false} strokeWidth={1.5} isAnimationActive={false} connectNulls name="heldC" />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ flex: '0 0 170px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
          {afterExit ? (
            <>
              {/* v0.63.0: upl/captured on after-exit rows are IF-HELD numbers —
                  the readout says what the real exit booked vs what holding
                  would be worth right now; never added to any P&L */}
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: (last.exit_pnl_per_ct ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                Out at ${last.exit_debit != null ? last.exit_debit.toFixed(2) : '—'}
                {last.exit_pnl_per_ct != null ? ` (${last.exit_pnl_per_ct >= 0 ? '+' : '−'}$${Math.abs(last.exit_pnl_per_ct).toFixed(0)}/ct)` : ''}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                if held now ${last.mid != null ? last.mid.toFixed(2) : '—'}
                {last.if_held_pnl_per_ct != null ? ` (${last.if_held_pnl_per_ct >= 0 ? '+' : '−'}$${Math.abs(last.if_held_pnl_per_ct).toFixed(0)}/ct)` : ''}
              </Typography>
              {last.if_held_vs_exit_per_ct != null && (
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: last.if_held_vs_exit_per_ct <= 0 ? 'success.main' : 'warning.main' }}>
                  {last.if_held_vs_exit_per_ct <= 0
                    ? `exit saved $${Math.abs(last.if_held_vs_exit_per_ct).toFixed(0)}/ct`
                    : `held would be +$${last.if_held_vs_exit_per_ct.toFixed(0)}/ct better`}
                </Typography>
              )}
              {last.if_held_goal_hit_at && <Typography variant="caption" sx={{ color: 'text.secondary' }}>goal touched {last.if_held_goal_hit_at}</Typography>}
              {last.if_held_stop_hit_at && <Typography variant="caption" sx={{ color: 'error.main' }}>stop touched {last.if_held_stop_hit_at}</Typography>}
            </>
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#66bb6a' }}>
                {toGoalC != null ? `${toGoalC}¢ to goal` : 'repricing…'}
              </Typography>
              <Typography variant="body2">
                {cents(last.mid)} of {cents(last.entry_credit)} captured{capturedPct != null ? ` (${capturedPct}%)` : ''}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: (last.upl ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                {last.upl != null ? `${last.upl >= 0 ? '+' : '−'}$${Math.abs(last.upl).toFixed(0)}` : '—'}
              </Typography>
              {givingBack && <Chip label="giving back" size="small" color="warning" sx={{ width: 'fit-content', fontWeight: 'bold' }} />}
              {last.giveback_armed && <Typography variant="caption" sx={{ color: 'text.secondary' }}>giveback shadow armed</Typography>}
            </>
          )}
        </Box>
      </Box>

      {/* 2 — SPY vs the lines (the structure chart) */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexWrap: 'wrap', mt: 1 }}>
        <Box sx={{ flex: '1 1 480px', minWidth: 320 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            SPY vs the exit lines
          </Typography>
          <ResponsiveContainer width="100%" height={190}>
            <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 2, left: 0 }}>
              <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={hhmm} tick={{ fontSize: 10 }} />
              <YAxis domain={[Math.floor(spotMin), Math.ceil(spotMax)]} tick={{ fontSize: 10 }} width={44} tickFormatter={(v) => Number(v).toFixed(0)} />
              <Tooltip labelFormatter={(t) => `${hhmm(t as number)} ET`} formatter={(v: any) => [Number(v).toFixed(2), 'SPY']} />
              {/* shade the safe side green */}
              {last.exit_level != null && (
                <ReferenceArea
                  y1={isPut ? last.exit_level : Math.floor(spotMin)}
                  y2={isPut ? Math.ceil(spotMax) : last.exit_level}
                  fill="#66bb6a" fillOpacity={0.06}
                />
              )}
              {last.exit_level != null && (
                <ReferenceLine y={last.exit_level} stroke="#ef5350"
                  label={{ value: `exit if SPY ${isPut ? '≤' : '≥'} ${last.exit_level}${last.exit_level_kind ? ` (${last.exit_level_kind.replace(/_/g, ' ')})` : ''}`, fontSize: 10, fill: '#ef5350', position: 'insideBottomLeft' }} />
              )}
              {last.short_strike != null && (
                <ReferenceLine y={last.short_strike} stroke="#b71c1c"
                  label={{ value: `short ${last.short_strike}`, fontSize: 10, fill: '#b71c1c', position: 'insideTopLeft' }} />
              )}
              {last.put_wall != null && <ReferenceLine y={last.put_wall} stroke="rgba(239,83,80,0.3)" strokeDasharray="3 3" label={{ value: `put wall ${last.put_wall}`, fontSize: 9, fill: 'rgba(239,83,80,0.5)', position: 'insideRight' }} />}
              {last.call_wall != null && <ReferenceLine y={last.call_wall} stroke="rgba(102,187,106,0.3)" strokeDasharray="3 3" label={{ value: `call wall ${last.call_wall}`, fontSize: 9, fill: 'rgba(102,187,106,0.5)', position: 'insideRight' }} />}
              {last.flip != null && <ReferenceLine y={last.flip} stroke="rgba(255,255,255,0.25)" strokeDasharray="3 3" label={{ value: `flip ${last.flip}`, fontSize: 9, fill: 'rgba(255,255,255,0.4)', position: 'insideRight' }} />}
              {/* v0.63.0: same exit marker as the money chart, for alignment */}
              {exitMs != null && (
                <ReferenceLine x={exitMs} stroke="#e0e0e0" strokeDasharray="2 2"
                  label={{ value: `out ${last.exit_at ? hhmm(toMs(last.exit_at)) : ''}`, fontSize: 10, fill: '#e0e0e0', position: 'insideTopRight' }} />
              )}
              <Line type="monotone" dataKey="spot" stroke="#ffb74d" dot={false} strokeWidth={2} isAnimationActive={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ flex: '0 0 170px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: nearExit ? 'error.main' : 'success.main' }}>
            {exitGapPts != null
              ? `${exitGapPts >= 0 ? '+' : '−'}${Math.abs(exitGapPts).toFixed(1)} pts (${Math.abs(exitGapPct!).toFixed(2)}%) ${isPut ? 'above' : 'below'} the exit line`
              : '—'}
          </Typography>
          {nearExit && <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>within 0.15% of the exit level</Typography>}
        </Box>
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
        Informational — the engine's exits are mechanical and already running. The Trade log remains the record of what happened.
      </Typography>
    </Paper>
  );
};

export default LiveTradeTracker;
